/**
 * SubscriptionGuard — shows a warning banner for past_due / trial-ending
 * subscriptions, and redirects `past_due` users to /billing (with a short
 * grace window) so they can update payment.
 *
 * Does NOT block access for trial users — trials are a feature, not a penalty.
 * Does block access only when status is 'past_due' after 3+ days OR 'canceled'.
 */

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, CreditCard } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useSubscription } from "@/hooks/use-subscription";

const GRACE_PERIOD_DAYS = 3;
const BILLING_PATH = "/billing";
const ALLOWED_PAST_DUE_PATHS = [BILLING_PATH, "/settings", "/auth"];

function msDaysAgo(date: string): number {
  return Math.floor((Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));
}

export function SubscriptionGuard({ children }: { children: React.ReactNode }) {
  const { subscription, isLoading } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isLoading || !subscription) return;

    const status = subscription.status;
    const path = location.pathname;

    // Don't bounce users who are already on billing / settings / auth
    if (ALLOWED_PAST_DUE_PATHS.some((p) => path.startsWith(p))) return;

    // Hard redirect: subscription explicitly canceled
    if (status === "canceled") {
      navigate(BILLING_PATH, { replace: true });
      return;
    }

    // Hard redirect: past_due for more than grace period
    if (status === "past_due" && subscription.updated_at) {
      const daysPastDue = msDaysAgo(subscription.updated_at);
      if (daysPastDue >= GRACE_PERIOD_DAYS) {
        navigate(BILLING_PATH, { replace: true });
        return;
      }
    }
  }, [subscription, isLoading, location.pathname, navigate]);

  if (isLoading || !subscription) return <>{children}</>;

  const status = subscription.status;
  const showPastDueBanner = status === "past_due";
  const showTrialBanner =
    status === "trialing" &&
    subscription.trial_end &&
    msDaysAgo(subscription.trial_end) > -3; // <= 3 days left

  return (
    <>
      {showPastDueBanner && (
        <Alert className="rounded-none border-x-0 border-t-0 border-b border-destructive/50 bg-destructive/10 text-destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>
              Your last payment failed. Update your card to avoid losing access.
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(BILLING_PATH)}
              className="shrink-0"
            >
              <CreditCard className="w-4 h-4 mr-1.5" /> Update payment
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {showTrialBanner && !showPastDueBanner && (
        <Alert className="rounded-none border-x-0 border-t-0 border-b border-warning/50 bg-warning/10">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>
              Trial ends{" "}
              {subscription.trial_end
                ? new Date(subscription.trial_end).toLocaleDateString()
                : "soon"}
              . Add a payment method to keep everything.
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(BILLING_PATH)}
              className="shrink-0"
            >
              <CreditCard className="w-4 h-4 mr-1.5" /> Add payment
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {children}
    </>
  );
}
